/**
 * Agent consent policy — Zod schemas (the tenant maker-checker + re-consent policy).
 *
 * Mirrors the backend agentconsentpolicy handler:
 *   GET/PUT/DELETE /api/v1/tenants/{tenant_id}/agent-consent-policy
 *
 * Two independent controls over operator-provisioned agent delegations:
 *   - maker-checker: high-value operator-provisioned delegations require the delegating
 *     user's own fresh passkey consent (the "maker" operator can't also be the "checker").
 *     `maker_checker_threshold` is the money floor above which the second-person consent is
 *     required, expressed as an integer in the currency's SMALLEST unit (minor units / "cents");
 *     null means "every operator-provisioned delegation needs consent" when the toggle is on.
 *   - re-consent deadline: an RFC3339 instant after which legacy delegations that never carried
 *     a user consent are suspended. null means no deadline (legacy grants keep running).
 *
 * `configured` is true once the tenant has saved a policy row (the opt-in signal); DELETE clears
 * the row and reverts to platform defaults (configured=false).
 */

import { z } from "zod";

export const agentConsentPolicySchema = z.object({
  // Whether high-value operator-provisioned delegations require the user's fresh passkey consent.
  require_maker_checker: z.boolean(),
  // The money floor (integer, currency minor units) above which consent is required; null = all.
  maker_checker_threshold: z.number().int().nonnegative().nullable(),
  // The RFC3339 instant after which un-consented legacy delegations are suspended; null = none.
  reconsent_deadline: z.string().nullable(),
  // True once the tenant has saved a consent policy — until then platform defaults apply.
  configured: z.boolean(),
});
export type AgentConsentPolicy = z.infer<typeof agentConsentPolicySchema>;

/** The PUT body: the three writable fields (never `configured`, which the server derives). */
export interface AgentConsentPolicyUpdate {
  require_maker_checker: boolean;
  maker_checker_threshold: number | null;
  reconsent_deadline: string | null;
}
